import { EtiquetaDePagina, CLASE_TITULO_DE_PAGINA } from '@/components/patterns';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AppLayout } from '@/components/layout/app-layout';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import {
  Copy,
  Check,
  ExternalLink,
  Code2,
  Terminal,
  Key,
  ShoppingCart,
  RefreshCw,
  Shield,
  ArrowRight,
  Download,
  Store,
} from 'lucide-react';
import { getPublicPlatformSettings } from '../services/platform-settings-api';
import { copyToClipboard } from '@/lib/utils/clipboard';

// ── Types ────────────────────────────────────────────────────────

type Lang = 'javascript' | 'python' | 'php' | 'curl';

// ── Helpers ──────────────────────────────────────────────────────

function resolveBaseUrl(fetched: string | null): string {
  if (fetched) return fetched;
  return window.location.origin.replace(':3000', ':4000');
}

function buildSnippets(baseUrl: string): Record<Lang, string> {
  return {
    javascript: `// Consultar precio por SKU
const response = await fetch('${baseUrl}/api/v1/price/SKU-001', {
  headers: {
    'X-API-Key': 'tu_api_key_aqui',
    'Content-Type': 'application/json',
  },
});
const data = await response.json();
console.log(data.price);

// Consulta en lote (batch)
const batch = await fetch('${baseUrl}/api/v1/prices', {
  method: 'POST',
  headers: {
    'X-API-Key': 'tu_api_key_aqui',
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({ skus: ['SKU-001', 'SKU-002', 'SKU-003'] }),
});
const batchData = await batch.json();
console.log(batchData.prices);`,

    python: `import requests

API_KEY = 'tu_api_key_aqui'
BASE_URL = '${baseUrl}'

# Consultar precio por SKU
response = requests.get(
    f'{BASE_URL}/api/v1/price/SKU-001',
    headers={'X-API-Key': API_KEY},
)
data = response.json()
print(data['price'])

# Consulta en lote (batch)
batch = requests.post(
    f'{BASE_URL}/api/v1/prices',
    headers={'X-API-Key': API_KEY, 'Content-Type': 'application/json'},
    json={'skus': ['SKU-001', 'SKU-002', 'SKU-003']},
)
print(batch.json()['prices'])`,

    php: `<?php
$apiKey = 'tu_api_key_aqui';
$baseUrl = '${baseUrl}';

// Consultar precio por SKU
$ch = curl_init("$baseUrl/api/v1/price/SKU-001");
curl_setopt_array($ch, [
    CURLOPT_RETURNTRANSFER => true,
    CURLOPT_HTTPHEADER => ["X-API-Key: $apiKey"],
]);
$data = json_decode(curl_exec($ch), true);
echo $data['price'];
curl_close($ch);

// Consulta en lote (batch)
$ch2 = curl_init("$baseUrl/api/v1/prices");
curl_setopt_array($ch2, [
    CURLOPT_RETURNTRANSFER => true,
    CURLOPT_POST => true,
    CURLOPT_POSTFIELDS => json_encode(['skus' => ['SKU-001', 'SKU-002', 'SKU-003']]),
    CURLOPT_HTTPHEADER => ["X-API-Key: $apiKey", "Content-Type: application/json"],
]);
$batch = json_decode(curl_exec($ch2), true);
print_r($batch['prices']);
curl_close($ch2);`,

    curl: `# Consultar precio por SKU
curl -H "X-API-Key: tu_api_key_aqui" \\
     ${baseUrl}/api/v1/price/SKU-001

# Consulta en lote (batch)
curl -X POST \\
     -H "X-API-Key: tu_api_key_aqui" \\
     -H "Content-Type: application/json" \\
     -d '{"skus":["SKU-001","SKU-002","SKU-003"]}' \\
     ${baseUrl}/api/v1/prices`,
  };
}

// ── Sub-components ───────────────────────────────────────────────

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await copyToClipboard(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={handleCopy}
      className="h-7 w-7 p-0 text-gray-400 hover:text-gray-200"
      title="Copiar al portapapeles"
    >
      {copied ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
    </Button>
  );
}

function CodeBlock({ code, lang }: { code: string; lang: Lang }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await copyToClipboard(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative rounded-lg bg-gray-900 dark:bg-gray-950">
      <div className="flex items-center justify-between border-b border-gray-700 px-4 py-2">
        <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">{lang}</span>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleCopy}
          className="h-7 gap-1.5 text-xs text-gray-400 hover:text-gray-200"
        >
          {copied ? (
            <>
              <Check className="h-3.5 w-3.5 text-green-400" />
              <span className="text-green-400">Copiado</span>
            </>
          ) : (
            <>
              <Copy className="h-3.5 w-3.5" />
              <span>Copiar</span>
            </>
          )}
        </Button>
      </div>
      <pre className="overflow-x-auto p-4 text-sm text-gray-300 leading-relaxed">
        <code>{code}</code>
      </pre>
    </div>
  );
}

// ── Main Page ────────────────────────────────────────────────────

export function IntegrationPage() {
  const [baseUrl, setBaseUrl] = useState<string>('');
  const [activeTab, setActiveTab] = useState<Lang>('javascript');

  useEffect(() => {
    getPublicPlatformSettings()
      .then((settings) => setBaseUrl(resolveBaseUrl(settings.apiBaseUrl)))
      .catch(() => setBaseUrl(resolveBaseUrl(null)));
  }, []);

  const snippets = baseUrl ? buildSnippets(baseUrl) : ({} as Record<Lang, string>);

  const tabs: { key: Lang; label: string }[] = [
    { key: 'javascript', label: 'JavaScript' },
    { key: 'python', label: 'Python' },
    { key: 'php', label: 'PHP' },
    { key: 'curl', label: 'cURL' },
  ];

  const endpoints = [
    {
      method: 'GET',
      path: '/api/v1/price/:sku',
      description: 'Obtiene el precio activo de un producto por SKU',
    },
    {
      method: 'POST',
      path: '/api/v1/prices',
      description: 'Consulta precios en lote (batch) — hasta 100 SKUs por llamada',
    },
    {
      method: 'GET',
      path: '/api/v1/products',
      description: 'Lista todos los productos con sus precios vigentes',
    },
    {
      method: 'POST',
      path: '/api/v1/catalog/sync',
      description: 'Sincroniza el catálogo de productos desde tu plataforma',
    },
    {
      method: 'GET',
      path: '/api/v1/status',
      description: 'Verifica el estado del servicio y la validez de la API Key',
    },
  ];

  const rateLimits = [
    { plan: 'Trial', perMin: '30', perDay: '1.000' },
    { plan: 'Basic', perMin: '100', perDay: '10.000' },
    { plan: 'Professional', perMin: '500', perDay: '50.000' },
    { plan: 'Enterprise', perMin: '2.000', perDay: '200.000' },
  ];

  const methodBadgeClass = (method: string) => {
    if (method === 'GET')
      return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
    if (method === 'POST')
      return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
    return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
  };

  return (
    <AppLayout>
      <div className="mx-auto max-w-5xl space-y-8 p-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-100 dark:bg-indigo-900/40">
            <Code2 className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div>
            <div className="mb-3">
              <EtiquetaDePagina tono="neutro">Integraciones</EtiquetaDePagina>
            </div>
            <h1 className={CLASE_TITULO_DE_PAGINA}>Integración API</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Conectá tu plataforma con Prixia en minutos
            </p>
          </div>
        </div>

        {/* How it works - Visual Flow */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Store className="h-5 w-5 text-indigo-500" />
              ¿Cómo funciona la integración?
            </CardTitle>
            <CardDescription>
              Conectá tu tienda online con Prixia para optimizar tus precios automáticamente
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Step 1 */}
              <div className="relative bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30 rounded-xl p-5 border border-blue-100 dark:border-blue-900/50">
                <div className="flex items-center gap-2 mb-3">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-600 text-sm font-bold text-white">
                    1
                  </span>
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    Tu tienda envía productos
                  </h3>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                  Tu tienda online le manda su catálogo de productos a Prixia por la API.
                </p>
                <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-blue-200 dark:border-blue-800">
                  <code className="text-xs text-blue-700 dark:text-blue-300 font-mono">
                    POST /api/v1/catalog/sync
                  </code>
                </div>
                <div className="hidden md:flex absolute -right-4 top-1/2 -translate-y-1/2 z-10">
                  <ArrowRight className="h-6 w-6 text-indigo-400" />
                </div>
              </div>

              {/* Step 2 */}
              <div className="relative bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-950/30 dark:to-purple-950/30 rounded-xl p-5 border border-indigo-100 dark:border-indigo-900/50">
                <div className="flex items-center gap-2 mb-3">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-indigo-600 text-sm font-bold text-white">
                    2
                  </span>
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    Prixia calcula los precios
                  </h3>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                  Prixia analiza la competencia, la demanda y los márgenes para calcular el precio
                  óptimo.
                </p>
                <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-indigo-200 dark:border-indigo-800">
                  <div className="flex items-center gap-2 text-xs text-indigo-700 dark:text-indigo-300">
                    <RefreshCw className="h-3.5 w-3.5" />
                    <span>Algoritmos de pricing</span>
                  </div>
                </div>
                <div className="hidden md:flex absolute -right-4 top-1/2 -translate-y-1/2 z-10">
                  <ArrowRight className="h-6 w-6 text-indigo-400" />
                </div>
              </div>

              {/* Step 3 */}
              <div className="bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-950/30 dark:to-green-950/30 rounded-xl p-5 border border-emerald-100 dark:border-emerald-900/50">
                <div className="flex items-center gap-2 mb-3">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-600 text-sm font-bold text-white">
                    3
                  </span>
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    Tu tienda recibe precios
                  </h3>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                  Tu e-commerce consulta los precios optimizados y los aplica automáticamente.
                </p>
                <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-emerald-200 dark:border-emerald-800">
                  <code className="text-xs text-emerald-700 dark:text-emerald-300 font-mono">
                    GET /api/v1/price/:sku
                  </code>
                </div>
              </div>
            </div>

            {/* Alternative for non-technical users */}
            <div className="mt-6 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800/50 rounded-lg p-4 flex gap-3">
              <Download className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800 dark:text-amber-300">
                <p className="font-medium">¿No tenés equipo técnico?</p>
                <p className="mt-1">
                  {/*
                    🔴 (20-oct) Decía que se bajaban «los precios recomendados» desde Precios Dinámicos,
                    con un enlace a /precios-dinamicos (no existe). El Excel de Productos trae tus precios
                    y los de la competencia; los propuestos salen en el Excel de cada corrida de estrategia.
                  */}
                  Cada corrida de una estrategia se descarga en Excel con sus precios propuestos, en{' '}
                  <Link
                    to="/compite/estrategia"
                    className="font-medium underline hover:no-underline"
                  >
                    Compite › Estrategia
                  </Link>
                  , para cargarlos a mano en tu plataforma. Y si tu tienda está conectada, «Aplicar
                  a tienda» en{' '}
                  <Link
                    to="/compite/productos"
                    className="font-medium underline hover:no-underline"
                  >
                    Compite › Productos
                  </Link>{' '}
                  le manda los precios sin programar nada.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Start */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Terminal className="h-5 w-5 text-indigo-500" />
              Guía de integración paso a paso
            </CardTitle>
            <CardDescription>Seguí estos pasos para conectar tu tienda con Prixia</CardDescription>
          </CardHeader>
          <CardContent>
            <ol className="space-y-6">
              <li className="flex gap-4">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-sm font-semibold text-white">
                  1
                </span>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">Generá tu API Key</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Andá a{' '}
                    <a
                      href="/claves-api"
                      className="text-indigo-600 hover:underline dark:text-indigo-400"
                    >
                      Claves API
                    </a>{' '}
                    y creá una nueva clave. Esta clave es tu credencial para acceder a la API.
                  </p>
                  <div className="mt-2 bg-gray-50 dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700 flex items-center gap-2">
                    <Key className="h-4 w-4 text-gray-400" />
                    <code className="text-xs text-gray-600 dark:text-gray-300 font-mono">
                      pk_live_xxxxxxxxxxxxxxxxxxxx
                    </code>
                  </div>
                </div>
              </li>
              <li className="flex gap-4">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-sm font-semibold text-white">
                  2
                </span>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">
                    Subí tu catálogo de productos
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Enviá tus productos a Prixia para que pueda analizarlos y calcular precios
                    óptimos. Incluí SKU, nombre, precio actual y costo.
                  </p>
                  <div className="mt-2 bg-gray-50 dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                    <code className="text-xs text-gray-600 dark:text-gray-300 font-mono">
                      POST /api/v1/catalog/sync
                    </code>
                    <p className="text-xs text-gray-400 mt-1">
                      Envía hasta 500 productos por llamada
                    </p>
                  </div>
                </div>
              </li>
              <li className="flex gap-4">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-sm font-semibold text-white">
                  3
                </span>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">
                    Consultá precios optimizados
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Una vez que Prixia procesó tu catálogo, consultá los precios recomendados para
                    cada producto y aplicalos en tu tienda.
                  </p>
                  <div className="mt-2 bg-gray-50 dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700 space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs font-semibold px-2 py-0.5 rounded">
                        GET
                      </span>
                      <code className="text-xs text-gray-600 dark:text-gray-300 font-mono">
                        /api/v1/price/:sku
                      </code>
                      <span className="text-xs text-gray-400">— Precio individual</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 text-xs font-semibold px-2 py-0.5 rounded">
                        POST
                      </span>
                      <code className="text-xs text-gray-600 dark:text-gray-300 font-mono">
                        /api/v1/prices
                      </code>
                      <span className="text-xs text-gray-400">— Hasta 100 precios a la vez</span>
                    </div>
                  </div>
                </div>
              </li>
              <li className="flex gap-4">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-sm font-semibold text-white">
                  4
                </span>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                    <ShoppingCart className="h-4 w-4" />
                    Actualizá tu tienda
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Usá el precio recomendado (
                    <code className="rounded bg-gray-100 px-1 text-xs dark:bg-gray-800">
                      recommendedPrice
                    </code>
                    ) de la respuesta para actualizar los precios en tu e-commerce. Podés
                    automatizar esto con un script que se ejecute periódicamente.
                  </p>
                </div>
              </li>
            </ol>
          </CardContent>
        </Card>

        {/* Authentication */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Shield className="h-5 w-5 text-indigo-500" />
              Autenticación
            </CardTitle>
            <CardDescription>
              Todas las llamadas a la API (excepto /status) requieren autenticación
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
                Incluí tu API Key en el header de cada petición:
              </p>
              <div className="bg-gray-900 rounded-lg px-4 py-3">
                <code className="text-sm text-gray-300">
                  <span className="text-blue-400">X-API-Key</span>:{' '}
                  <span className="text-green-400">pk_live_tu_clave_aqui</span>
                </code>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="flex items-start gap-2 bg-green-50 dark:bg-green-950/20 rounded-lg p-3 border border-green-200 dark:border-green-800/50">
                <Check className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-green-800 dark:text-green-300">
                  <span className="font-medium">200 OK</span> — Petición exitosa
                </div>
              </div>
              <div className="flex items-start gap-2 bg-red-50 dark:bg-red-950/20 rounded-lg p-3 border border-red-200 dark:border-red-800/50">
                <Key className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-red-800 dark:text-red-300">
                  <span className="font-medium">401 Unauthorized</span> — API Key inválida o
                  faltante
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Base URL */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Base URL</CardTitle>
            <CardDescription>
              Todas las llamadas a la API deben incluir este prefijo
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-800/50">
              <code className="flex-1 text-sm font-mono text-gray-800 dark:text-gray-200">
                {baseUrl || 'Cargando…'}
              </code>
              {baseUrl && <CopyButton text={baseUrl} />}
            </div>
          </CardContent>
        </Card>

        {/* Code Snippets */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Ejemplos de código</CardTitle>
            <CardDescription>Snippets listos para usar en tu lenguaje preferido</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Tabs */}
            <div className="flex gap-1 rounded-lg border border-gray-200 bg-gray-100 p-1 dark:border-gray-700 dark:bg-gray-800">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                    activeTab === tab.key
                      ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-white'
                      : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Code */}
            {baseUrl && <CodeBlock code={snippets[activeTab]} lang={activeTab} />}
          </CardContent>
        </Card>

        {/* Endpoints */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Endpoints disponibles</CardTitle>
            <CardDescription>Referencia rápida de los endpoints de la API pública</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="pb-3 text-left font-semibold text-gray-700 dark:text-gray-300 w-16">
                      Método
                    </th>
                    <th className="pb-3 pl-4 text-left font-semibold text-gray-700 dark:text-gray-300">
                      Ruta
                    </th>
                    <th className="pb-3 pl-4 text-left font-semibold text-gray-700 dark:text-gray-300">
                      Descripción
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {endpoints.map((ep) => (
                    <tr key={ep.path} className="group">
                      <td className="py-3">
                        <span
                          className={`inline-block rounded px-2 py-0.5 text-xs font-semibold ${methodBadgeClass(ep.method)}`}
                        >
                          {ep.method}
                        </span>
                      </td>
                      <td className="py-3 pl-4">
                        <code className="font-mono text-gray-800 dark:text-gray-200">
                          {ep.path}
                        </code>
                      </td>
                      <td className="py-3 pl-4 text-gray-500 dark:text-gray-400">
                        {ep.description}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Swagger link */}
            {baseUrl && (
              <div className="mt-4 flex items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-3 dark:border-indigo-800/50 dark:bg-indigo-900/20">
                <ExternalLink className="h-4 w-4 shrink-0 text-indigo-600 dark:text-indigo-400" />
                <span className="text-sm text-indigo-800 dark:text-indigo-300">
                  Documentación interactiva (Swagger UI):{' '}
                  <a
                    href={`${baseUrl}/api/v1/docs`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium underline underline-offset-2 hover:no-underline"
                  >
                    {baseUrl}/api/v1/docs
                  </a>
                </span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Rate Limits */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Límites de uso</CardTitle>
            <CardDescription>Solicitudes permitidas según el plan de la API Key</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="pb-3 text-left font-semibold text-gray-700 dark:text-gray-300">
                      Plan
                    </th>
                    <th className="pb-3 pl-4 text-left font-semibold text-gray-700 dark:text-gray-300">
                      Por minuto
                    </th>
                    <th className="pb-3 pl-4 text-left font-semibold text-gray-700 dark:text-gray-300">
                      Por día
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {rateLimits.map((row) => (
                    <tr key={row.plan}>
                      <td className="py-3 font-medium text-gray-900 dark:text-white">{row.plan}</td>
                      <td className="py-3 pl-4 text-gray-600 dark:text-gray-400">
                        {row.perMin} req/min
                      </td>
                      <td className="py-3 pl-4 text-gray-600 dark:text-gray-400">
                        {row.perDay} req/día
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
